import { MondayTaskElement } from "../schema";
import { IMondayTaskController } from "../controller";
import { ElementProps } from "elements/base/provider";
import { Group } from "react-konva";
import useObservableController from "elements/hooks/use-observable-controller";
import { MONDAY_TASK_WIDTH, MONDAY_TASK_BASE_HEIGHT } from "shared/datamodel/monday-task";
import { PinnedColumns } from "./pinned-columns/pinned-columns";
import { MondayTaskHeader } from "./header/header";
import { FilePreviews } from "./file-previews/file-previews";
import { ColumnDetails } from "./column-details/column-details";
import { Files } from "./files/files";
import { Updates } from "./updates/updates";
import { BORDER_HEIGHT, PADDING } from "../consts";
import { useMondayTaskControllerSetup } from "../hooks/use-monday-task-controller-setup";
import { Trait, ITraits } from "frontend/canvas-designer-new/elements-toolbar/elements-toolbar-types";
import Container from "frontend/ui-components/konva-helpers/container";
import { MondayTaskBackground } from "./monday-task-background";
import { MondayTaskLoading } from "./monday-task-loading";
import { MondayTaskError } from "./error/monday-task-error";
import { MondayErrorCodes } from "shared/integrations/monday-types";

export default function MondayTaskElementComponent({ controller: _controller }: ElementProps<MondayTaskElement>) {
  const controller = _controller as IMondayTaskController;
  useObservableController(controller);

  const { element, context } = controller;
  const {
    width = MONDAY_TASK_WIDTH,
    height = MONDAY_TASK_BASE_HEIGHT,
    mondayItemId,
    integrationId,
    expanded = false,
  } = element;

  const contentWidth = width - 2 * PADDING;

  // Set up controller with integration data and mutate function
  useMondayTaskControllerSetup({ controller, integrationId, mondayItemId });

  if (!context.isLoggedIn) {
    return (
      <Container width={width} height={height + BORDER_HEIGHT}>
        <MondayTaskError width={width} height={height + BORDER_HEIGHT} code={MondayErrorCodes.LOGIN_REQUIRED} />
      </Container>
    );
  }

  if (controller.isLoading()) {
    return (
      <Container width={width} height={height + BORDER_HEIGHT}>
        <MondayTaskLoading width={width} height={height + BORDER_HEIGHT} />
      </Container>
    );
  }

  const error = controller.getError();
  if (error !== null || !controller.getItem()) {
    const code = error?.code ?? MondayErrorCodes.NOT_FOUND;
    const url = error?.url;
    return (
      <Container width={width} height={height + BORDER_HEIGHT}>
        <MondayTaskError width={width} height={height + BORDER_HEIGHT} code={code} url={url} />
      </Container>
    );
  }

  return (
    <Container width={width} height={height + BORDER_HEIGHT}>
      <MondayTaskBackground controller={controller} />

      {/* Content area */}
      <Group x={PADDING} y={BORDER_HEIGHT + PADDING}>
        <FilePreviews controller={controller} contentWidth={contentWidth} />

        <MondayTaskHeader controller={controller} allowExpand={!context.isReadOnly} contentWidth={contentWidth} />

        <PinnedColumns controller={controller} contentWidth={contentWidth} />
      </Group>

      {expanded && (
        <>
          <ColumnDetails controller={controller} />
          <Files controller={controller} />
          <Updates controller={controller} />
        </>
      )}
    </Container>
  );
}

export function mondayTaskTraits(element: MondayTaskElement): ITraits {
  const { fill, colorBy } = element;
  return { mondayTaskColor: { fill, colorBy } };
}

// Value is typed as any since it's a union of all possible trait values
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function mondayTaskValidateTrait(_element: MondayTaskElement, trait: Trait, value: any) {
  if (trait == Trait.mondayTaskColor) {
    return { fill: value.fill, colorBy: value.colorBy };
  }

  return value;
}
